// Parser de XML NFe (modelo 55) 100% client-side, sem dependencias externas.
// Layout estavel ha anos no portal nacional — campos extraidos:
//   ide:    nNF, serie, infNFe[@Id] (chave de 44 digitos, prefixada com "NFe")
//   emit:   CNPJ, xNome (fornecedor)
//   det[]:  prod.cProd, prod.cEAN, prod.xProd, prod.NCM, prod.CFOP,
//           prod.uCom, prod.qCom, prod.vUnCom
//           imposto/ICMS/{ICMS*|ICMSSN*} → CST ou CSOSN

export interface NfeItem {
  cProd: string
  cEAN: string | null
  descricao: string
  ncm: string
  cfop: string
  unidade: string
  quantidade: number
  valor_unitario: number
  cst_csosn: string | null
}

export interface NfeParsed {
  chave: string
  numero: string
  serie: string
  fornecedor: { cnpj: string; nome: string }
  itens: NfeItem[]
}

function text(parent: Element | null, tag: string): string {
  if (!parent) return ''
  const el = parent.getElementsByTagName(tag)[0]
  return el?.textContent?.trim() ?? ''
}

function num(parent: Element | null, tag: string): number {
  const v = text(parent, tag)
  if (!v) return 0
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

/** Procura CST ou CSOSN dentro de qualquer node filho do bloco ICMS. */
function extractCstCsosn(impostoIcms: Element | null): string | null {
  if (!impostoIcms) return null
  // ICMS00, ICMS10, ... carregam <CST>; ICMSSN101, ICMSSN102, ... carregam <CSOSN>.
  const cst = impostoIcms.getElementsByTagName('CST')[0]?.textContent?.trim()
  if (cst) return cst
  const csosn = impostoIcms.getElementsByTagName('CSOSN')[0]?.textContent?.trim()
  if (csosn) return csosn
  return null
}

export function parseNfeXml(xmlText: string): NfeParsed {
  const parser = new DOMParser()
  const doc = parser.parseFromString(xmlText, 'text/xml')

  const parserError = doc.getElementsByTagName('parsererror')[0]
  if (parserError) {
    throw new Error('XML invalido: nao foi possivel fazer o parse')
  }

  const infNFe = doc.getElementsByTagName('infNFe')[0]
  if (!infNFe) {
    throw new Error('XML nao parece ser uma NFe (sem tag infNFe)')
  }

  // Chave vem do atributo Id, no formato "NFe<44 digitos>"
  const idAttr = infNFe.getAttribute('Id') ?? ''
  const chave = idAttr.replace(/^NFe/, '')

  const ide = infNFe.getElementsByTagName('ide')[0] ?? null
  const emit = infNFe.getElementsByTagName('emit')[0] ?? null

  const numero = text(ide, 'nNF')
  const serie = text(ide, 'serie')
  const cnpj = text(emit, 'CNPJ')
  const nome = text(emit, 'xNome')

  const detEls = Array.from(infNFe.getElementsByTagName('det'))
  const itens: NfeItem[] = detEls.map(det => {
    const prod = det.getElementsByTagName('prod')[0] ?? null
    const icmsBlock = det.getElementsByTagName('ICMS')[0] ?? null
    const cEAN = text(prod, 'cEAN')
    return {
      cProd: text(prod, 'cProd'),
      cEAN: cEAN && cEAN !== 'SEM GTIN' ? cEAN : null,
      descricao: text(prod, 'xProd'),
      ncm: text(prod, 'NCM'),
      cfop: text(prod, 'CFOP'),
      unidade: text(prod, 'uCom'),
      quantidade: num(prod, 'qCom'),
      valor_unitario: num(prod, 'vUnCom'),
      cst_csosn: extractCstCsosn(icmsBlock),
    }
  })

  return {
    chave,
    numero,
    serie,
    fornecedor: { cnpj, nome },
    itens,
  }
}
